import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Button } from '@thanx/uikit/button'
import { useStyletron } from '@thanx/uikit/theme'

import {
  createPicker,
  DEFAULT_GROUP_ID,
  removePicker,
  selectGroupDisabled,
  selectGroupLevel1Label,
  selectGroupLevel2Label,
  selectHasAnyTopSelection,
  selectPickerIdsForGroup,
} from 'slices/aiSegmentBuilder/picker'

import BottomSelectorContainer from './BottomSelector.container'
import OperatorContainer from './Operator.container'
import PickerContainer from './Picker.container'
import PickerGroupHeaderContainer from './PickerGroupHeader.container'
import TopSelectorContainer from './TopSelector.container'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface TwoLevelPickerGroupContainerProps {
  /** Unique identifier for this picker group */
  groupId?: string
  /** Whether the group has been hydrated from server data (skips auto-init) */
  hasHydrated?: boolean
  /** Callback when the group is confirmed */
  onConfirm?: (groupId: string) => void
  /** Custom styles for the container */
  style?: React.CSSProperties
}

// ═══════════════════════════════════════════════════════════════════════════
// PickerItem Component - Renders a single picker with its selectors
// ═══════════════════════════════════════════════════════════════════════════

interface PickerItemProps {
  pickerId: number
  /** Index of this picker within the group (0-based, used for display numbering) */
  index: number
  onRemove: (pickerId: number) => void
  canRemove: boolean
  disabled: boolean
  topPlaceholder: string
  bottomPlaceholder: string
}

const PickerItem = React.memo(
  ({
    pickerId,
    index,
    onRemove,
    canRemove,
    disabled,
    topPlaceholder,
    bottomPlaceholder,
  }: PickerItemProps) => {
    const [css] = useStyletron()

    const topRowClassName = css({
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      marginBottom: '12px',
    })

    const topSelectorWrapperClassName = css({
      flex: 1,
    })

    const removeHandler = useMemo(
      () => (canRemove && !disabled ? () => onRemove(pickerId) : undefined),
      [canRemove, disabled, onRemove, pickerId]
    )

    return (
      <PickerContainer
        id={pickerId}
        index={index}
        onRemove={removeHandler}
        disabled={disabled}
      >
        {/* Row 1: Top selector + operator */}
        <div className={topRowClassName}>
          <div className={topSelectorWrapperClassName}>
            <TopSelectorContainer
              pickerId={pickerId}
              placeholder={topPlaceholder}
              disabled={disabled}
            />
          </div>
          <OperatorContainer pickerId={pickerId} disabled={disabled} />
        </div>

        {/* Row 2: Bottom selector (direct child, receives pickerId via cloneElement) */}
        <BottomSelectorContainer placeholder={bottomPlaceholder} />
      </PickerContainer>
    )
  }
)

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for a group of pickers.
 * Displays all pickers belonging to the specified groupId.
 * Includes header, picker list, and action buttons.
 */
const TwoLevelPickerGroupContainer = React.memo(
  ({
    groupId = DEFAULT_GROUP_ID,
    hasHydrated = false,
    onConfirm,
    style,
  }: TwoLevelPickerGroupContainerProps) => {
    const dispatch = useDispatch()
    const [css, theme] = useStyletron()

    // Select picker IDs and disabled state for this group
    const pickerIds = useSelector((state: any) =>
      selectPickerIdsForGroup(state, groupId)
    )
    const disabled = useSelector((state: any) =>
      selectGroupDisabled(state, groupId)
    )

    // Get labels from group config
    const level1Label = useSelector((state: any) =>
      selectGroupLevel1Label(state, groupId)
    )
    const level2Label = useSelector((state: any) =>
      selectGroupLevel2Label(state, groupId)
    )

    // Derived labels
    const level1LabelLower = level1Label.toLowerCase()
    const level2LabelLower = level2Label.toLowerCase()
    const topPlaceholder = `Select ${
      level1LabelLower === 'item' ? 'an' : 'a'
    } ${level1LabelLower}...`
    const bottomPlaceholder = `Select ${level2LabelLower}...`

    const hasAnyTopSelection = useSelector((state: any) =>
      selectHasAnyTopSelection(state, groupId)
    )

    // Initialize first picker on mount (only if no pickers exist and not server-driven)
    useEffect(() => {
      if (!hasHydrated && pickerIds.length === 0) {
        dispatch(createPicker({ groupId }))
      }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Add a new picker
    const handleAddPicker = useCallback(() => {
      if (disabled) return
      dispatch(createPicker({ groupId }))
    }, [dispatch, groupId, disabled])

    // Remove a picker
    const handleRemovePicker = useCallback(
      (pickerId: number) => {
        if (disabled) return
        dispatch(removePicker({ pickerId }))
      },
      [dispatch, disabled]
    )

    // Confirm the group
    const handleConfirm = useCallback(() => {
      onConfirm?.(groupId)
    }, [onConfirm, groupId])

    // Whether pickers can be removed (need at least 1)
    const canRemove = useMemo(
      () => pickerIds.length > 1 && !disabled,
      [pickerIds.length, disabled]
    )

    // ═════════════════════════════════════════════════════════════════════════
    // Styles using theme tokens
    // ═════════════════════════════════════════════════════════════════════════

    const containerClassName = css({
      marginBottom: '24px',
      padding: '16px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.colors.grey30,
      borderRadius: '8px',
      backgroundColor: theme.colors.grey10,
    })

    const pickersListClassName = css({
      marginBottom: '16px',
    })

    const actionsClassName = css({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    })

    return (
      <div className={containerClassName} style={style}>
        <PickerGroupHeaderContainer groupId={groupId} disabled={disabled} />

        {/* Pickers list */}
        <div className={pickersListClassName}>
          {pickerIds.map((pickerId, index) => (
            <PickerItem
              key={pickerId}
              pickerId={pickerId}
              index={index}
              onRemove={handleRemovePicker}
              canRemove={canRemove}
              disabled={disabled}
              topPlaceholder={topPlaceholder}
              bottomPlaceholder={bottomPlaceholder}
            />
          ))}
        </div>

        {/* Actions */}
        <div className={actionsClassName}>
          <Button
            kind="secondary"
            onClick={handleAddPicker}
            disabled={disabled}
          >
            + Add {level1LabelLower}
          </Button>
          <Button
            kind="primary"
            onClick={handleConfirm}
            disabled={disabled || !hasAnyTopSelection}
          >
            Confirm
          </Button>
        </div>
      </div>
    )
  }
)

TwoLevelPickerGroupContainer.displayName = 'TwoLevelPickerGroupContainer'

export default TwoLevelPickerGroupContainer
